import { format, parseISO, subDays } from 'date-fns'
import type { RateCacheEntry } from '@/types/domain'
import {
  BocApiError,
  fetchFxUsdCadRange,
  type BocFxPoint,
} from '@/core/transport/boc-valet-client'
import { getRate, putRates } from '@/core/storage/rate-cache'
import {
  AdapterError,
  type CurrencyAdapter,
  type RateAgainstUsdResult,
} from './currency-adapter'

export const BOC_SERIES = 'FXUSDCAD' as const
export const BOC_LOOKBACK_DAYS = 7

export interface CadAdapterDeps {
  /** Injection seam for tests; defaults to the real Valet client. */
  fetchRange?: (start: string, end: string) => Promise<BocFxPoint[]>
  /** Returns the most recent CAD/USD publication on or before `date`. */
  cacheGet?: (date: string) => RateCacheEntry | undefined
  cachePut?: (entries: RateCacheEntry[]) => void
  now?: () => Date
}

export function createCadAdapter(deps: CadAdapterDeps = {}): CurrencyAdapter {
  const fetchRange = deps.fetchRange ?? fetchFxUsdCadRange
  const cacheGet =
    deps.cacheGet ??
    ((date: string) => getRate({ source: 'boc', series: BOC_SERIES, date }))
  const cachePut = deps.cachePut ?? putRates
  const fetchedAt = (): string => (deps.now?.() ?? new Date()).toISOString()

  return {
    code: 'CAD',
    async rateAgainstUSD(receiptDateIso: string): Promise<RateAgainstUsdResult> {
      const cached = cacheGet(receiptDateIso)
      if (cached) {
        return {
          rate: 1 / cached.value,
          effectiveDate: cached.effectiveDate,
          source: 'boc',
        }
      }

      const start = format(subDays(parseISO(receiptDateIso), BOC_LOOKBACK_DAYS), 'yyyy-MM-dd')
      const end = receiptDateIso

      let points: BocFxPoint[]
      try {
        points = await fetchRange(start, end)
      } catch (err) {
        throw asAdapterError(err)
      }

      if (points.length === 0) {
        throw new AdapterError(
          'SEM_PUBLICACAO_NO_INTERVALO',
          `BoC sem cotação FXUSDCAD em [${start}, ${end}]`,
        )
      }

      const lastPoint = points[points.length - 1] as BocFxPoint
      const ts = fetchedAt()
      const cacheEntries: RateCacheEntry[] = points.map((p) => ({
        source: 'boc',
        series: BOC_SERIES,
        date: p.date,
        value: p.value,
        effectiveDate: p.date,
        fetchedAt: ts,
      }))
      cacheEntries.push({
        source: 'boc',
        series: BOC_SERIES,
        date: receiptDateIso,
        value: lastPoint.value,
        effectiveDate: lastPoint.date,
        fetchedAt: ts,
      })
      cachePut(cacheEntries)

      return {
        rate: 1 / lastPoint.value,
        effectiveDate: lastPoint.date,
        source: 'boc',
      }
    },
  }
}

function asAdapterError(err: unknown): AdapterError {
  if (err instanceof BocApiError) {
    return new AdapterError(err.code, `CAD adapter: ${err.message}`, err)
  }
  return new AdapterError('NETWORK', err instanceof Error ? err.message : String(err), err)
}

/** Default real adapter wired against the live BoC client and shared cache. */
export const cadAdapter: CurrencyAdapter = createCadAdapter()
